import React, { useEffect, useState } from 'react';
import { Grid, ToggleButton, ToggleButtonGroup, Box, Typography } from "@mui/material";
import { parseDate } from "./utils";
import { acctMonthToLabel } from "./hooks";
import { CommonButton } from "../common/commonButton";
import { numberFormatter } from "../../utils/generalFunctions";
import format from "date-fns/format";
import LinkIcon from '@mui/icons-material/Link';
import { useSelector } from "react-redux";
import { customDateFormat, getPaginationCount } from "../CashAllocation/utils";

const Container = {
  flexGrow: 1,
  justifyContent: "flex-end",
  alignItems: "flex-end",
  display: "flex",
};

const AllocationDetails = ({
  policy,
  handleClose,
  handleSave,
  transaction,
  currentRowMode,
  associatedActivities,
  apiProgressBar,
  setApiProgressBar,
  setOpenDialogueId,
  setViaHistoryLink,
  setDialogueAllocation,
  saveDisable
}) => {
  const [activeSection, setActiveSection] = useState('information');
  const { userData } = useSelector((state) => state?.user);

  const handleSectionChange = (event, newSection) => {
    if (newSection !== null) {
      setActiveSection(newSection);
    }
  };

  const openActivityViaHistoryLink = (activity_type) => {
    setOpenDialogueId(activity_type);
    setDialogueAllocation(policy);
    setViaHistoryLink(true);
  }

  const renderInformationSection = () => {
    const fieldStyle = {
      label: {
        color: "#666",
        fontWeight: 500,
        fontSize: "14px",
        mb: 0.5,
        fontFamily: "'Inter', 'Poppins', sans-serif",
        textAlign: "left"
      },
      value: {
        fontWeight: 600,
        fontSize: "14px",
        color: "#333",
        fontFamily: "'Inter', 'Poppins', sans-serif",
        textAlign: "left"
      }
    };

    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <Grid container spacing={3} sx={{ width: '100%', px: 3, alignItems: 'flex-start' }}>
          {/* Column 1 - 5 entries */}
          <Grid item md={3} sx={{ px: 2 }}>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>Policy Number</Typography>
              <Typography sx={fieldStyle.value}>{policy?.policy_id || ""}</Typography>
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>UMR</Typography>
              <Typography sx={fieldStyle.value}>{policy?.allocation_umr || ""}</Typography>
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>Master Binder</Typography>
              <Typography sx={fieldStyle.value}>{policy?.allocation_binder || ""}</Typography>
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>Binding Agreement</Typography>
              <Typography sx={fieldStyle.value}>{policy?.binding_agreement || ""}</Typography>
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>Producing Entity Name</Typography>
              <Typography sx={fieldStyle.value}>{policy?.policy_fk?.Producing_Entity || ""}</Typography>
            </Box>
          </Grid>

          {/* Column 2 - 5 entries */}
          <Grid item md={3} sx={{ px: 2 }}>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>SCM Partner Name</Typography>
              <Typography sx={fieldStyle.value}>
                {policy?.policy_fk?.SCM_Partner === "nan" ? "" : policy?.policy_fk?.SCM_Partner || ""}
              </Typography>
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>EEA/Non EEA</Typography>
              <Typography sx={fieldStyle.value}>
                {policy?.allocation_eea?.toUpperCase() ?? (
                  (policy?.policy_fk?.Producing_Entity === "MEEA" || policy?.policy_fk?.Producing_Entity === "MEU2")
                    ? "EEA"
                    : ""
                )}
              </Typography>
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>Accounting Month</Typography>
              <Typography sx={fieldStyle.value}>
                {policy?.accounting_monthyear ? acctMonthToLabel(policy?.accounting_monthyear) : ""}
              </Typography>
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>Associated Activities</Typography>
              {associatedActivities && Object.keys(associatedActivities).length > 0 && (
                <Typography sx={fieldStyle.value}>
                  {Object.keys(associatedActivities).join(", ")}
                </Typography>
              )}
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>Allocated Date</Typography>
              <Typography sx={fieldStyle.value}>
                {policy?.allocation_date ? customDateFormat(policy?.allocation_date) : ""}
              </Typography>
            </Box>
          </Grid>

          {/* Column 3 - 5 entries */}
          <Grid item md={3} sx={{ px: 2 }}>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>Invoice Verification</Typography>
              <Typography sx={fieldStyle.value}>{policy?.allocation_invoice_verification || ""}</Typography>
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>XFI Batch Number</Typography>
              <Typography sx={fieldStyle.value}>{policy?.XFIbatchno || ""}</Typography>
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>GXB Batch Number</Typography>
              <Typography sx={fieldStyle.value}>{policy?.GXPbatchno || ""}</Typography>
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>Instalment Number</Typography>
              <Typography sx={fieldStyle.value}>
                {policy?.policy_installment_number || policy?.installment_number || ""}
              </Typography>
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>Instalment Due Date</Typography>
              <Typography sx={fieldStyle.value}>
                {policy?.policy_fk?.Installment_Due_date || policy?.installment_duedate || ""}
              </Typography>
            </Box>
          </Grid>

          {/* Column 4 - 5 entries */}
          <Grid item md={3} sx={{ px: 2 }}>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>Original Amount</Typography>
              <Typography sx={fieldStyle.value}>
                {numberFormatter(policy?.policy_fk?.Installment_Agency_Amount_in_Orig ?? 0)}
              </Typography>
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>Settlement Currency</Typography>
              <Typography sx={fieldStyle.value}>
                {policy?.settlement_currency ?? policy?.policy_fk?.Settlement_Ccy ?? ""}
              </Typography>
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>XFI Variance Amount</Typography>
              <Typography sx={fieldStyle.value}>
                {numberFormatter(policy?.XFI_Variance_Amount ?? 0)}
              </Typography>
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>GXB Amount</Typography>
              <Typography sx={fieldStyle.value}>
                {numberFormatter(policy?.GXBAmount ?? 0)}
              </Typography>
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>USD Receivable Amount</Typography>
              <Typography sx={fieldStyle.value}>
                {numberFormatter(policy?.cash_tracker_data?.Receivable_Amount_calculated || 0)}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Additional fields row - aligned with columns above */}
        <Grid container spacing={3} sx={{ width: '100%', px: 3, alignItems: 'flex-start' }}>
          <Grid item md={3} sx={{ px: 2 }}>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>USD Allocated Amount</Typography>
              <Typography sx={fieldStyle.value}>
                {numberFormatter(policy?.cash_tracker_data?.Allocated_Amount_calculated || 0)}
              </Typography>
            </Box>
          </Grid>
          <Grid item md={3} sx={{ px: 2 }}>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldStyle.label}>USD Remaining Balance Amount</Typography>
              <Typography sx={fieldStyle.value}>
                {numberFormatter(policy?.cash_tracker_data?.Remaining_Balance_usd || 0)}
              </Typography>
            </Box>
          </Grid>
          <Grid item md={6} sx={{ px: 2, display: "flex", justifyContent: "flex-end", alignItems: "flex-start" }}>
            <Box sx={{ display: "flex", gap: 1.5, mb: 2.5 }}>
              <CommonButton
                text="Cancel"
                handleClick={() => handleClose("")}
                sx={{ 
                  backgroundColor: "transparent !important", 
                  color: "#1672DE !important",
                  border: "1px solid #1672DE",
                  borderRadius: "6px",
                  padding: "10px 20px",
                  fontSize: "14px",
                  fontWeight: 500,
                  fontFamily: "'Inter', 'Poppins', sans-serif",
                  "&:hover": {
                    backgroundColor: "rgba(22, 114, 222, 0.1) !important",
                  }
                }}
                variant="outlined"
              />
              {(userData?.role?.toLowerCase() === "manager" || currentRowMode === "EDIT") && (
                <CommonButton
                  text="Save"
                  handleClick={handleSave}
                  sx={{ 
                    borderRadius: "6px",
                    backgroundColor: "#1672DE !important",
                    color: "#ffffff !important",
                    padding: "10px 20px",
                    fontSize: "14px",
                    fontWeight: 500,
                    fontFamily: "'Inter', 'Poppins', sans-serif",
                    "&:hover": {
                      backgroundColor: "#1565c0 !important",
                    }
                  }}
                  hoverColor="#1565c0"
                  disabled={apiProgressBar && !saveDisable}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderAuditSection = () => (
        <Grid container spacing={2} mt={2} paddingLeft={2} paddingRight={2} sx={{ visibility: 'visible', display: 'flex', width: '100%', color: '#333' }}>
          <Grid item xs={12} sx={{ visibility: 'visible', display: 'block', width: '100%' }}>
            <table style={{
              width: '100%', 
              borderCollapse: 'collapse', 
              visibility: 'visible', 
              display: 'table',
              fontFamily: "'Inter', 'Poppins', sans-serif",
              fontSize: "14px"
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ textAlign:'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>Changed Field</th>
                  <th style={{ textAlign:'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>Old Value</th>
                  <th style={{ textAlign:'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>New Value</th>
                  <th style={{ textAlign:'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>Changed By</th>
                  <th style={{ textAlign:'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>Last Edit Time</th>
                  <th style={{ textAlign:'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {policy?.cash_allocation_audit_data?.map((audit, index) => (
                  <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff' }}>
                    <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{audit.field_name}</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{audit.old_value}</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{audit.new_value}</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{audit.changed_by}</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{audit.current_edit_datetime}</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{audit.event_type === 'add' ? 'Added cash allocation' : audit.event_type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Grid>
    </Grid>
  );

  const renderAssociatedActivitiesSection = () => (
    <Grid container spacing={2} mt={2} paddingLeft={2} paddingRight={2} sx={{ visibility: 'visible', display: 'flex', width: '100%', color: '#333' }}>
      <Grid item xs={12} sx={{ visibility: 'visible', display: 'block', width: '100%' }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse', 
          visibility: 'visible', 
          display: 'table',
          fontFamily: "'Inter', 'Poppins', sans-serif",
          fontSize: "14px"
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th style={{ textAlign: 'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>Activity Name</th>
              <th style={{ textAlign: 'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>Reason Type</th>
              <th style={{ textAlign: 'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>Amount</th>
              <th style={{ textAlign: 'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>WF Status</th>
              <th style={{ textAlign: 'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>Created By</th>
              <th style={{ textAlign: 'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>Created At</th>
              <th style={{ textAlign: 'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>Workflow Link</th>
            </tr>
          </thead>
          <tbody>
            {associatedActivities && Object.keys(associatedActivities).sort().map((key, index) => {
              if (key == "Correction type") {
                const values = associatedActivities[key];
                const latest = values.reduce((latest, current) => {
                  return new Date(current.created_at) > new Date(latest.created_at) ? current : latest;
                }, values[0]);
                return (
                  <>
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff' }}>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{"Correction Reason"}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.issue_category}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.cash_allocation?.receivable_amt}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.workflow_status}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.cash_allocation?.created_by?.user_name ?? ""}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{format(latest.created_at, "dd-MM-yyyy HH:mm:ss")}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }} onClick={() => openActivityViaHistoryLink("correction-types")}><LinkIcon /></td>
                    </tr>
                  </>
                )
              }

              else if (key == "Write off") {
                const values = associatedActivities[key];
                const latest = values.reduce((latest, current) => {
                  return new Date(current.created_at) > new Date(latest.created_at) ? current : latest;
                }, values[0]);
                return (
                  <>
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff' }}>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{"Write-Off"}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.writeoff_reason}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.writeoff_amt}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.workflow_status}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.cash_allocation?.created_by?.user_name ?? ""}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{format(latest.created_at, "dd-MM-yyyy HH:mm:ss")}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }} onClick={() => openActivityViaHistoryLink("write-off")}><LinkIcon /></td>
                    </tr>
                  </>
                )
              }

              else if (key == "Refund") {
                const values = associatedActivities[key];
                const latest = values.reduce((latest, current) => {
                  return new Date(current.created_at) > new Date(latest.created_at) ? current : latest;
                }, values[0]);
                return (
                  <>
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff' }}>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{"Payment"}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.refund_reason}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.refund_amt}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.workflow_status}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.cash_allocation?.created_by?.user_name ?? ""}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{format(latest.created_at, "dd-MM-yyyy HH:mm:ss")}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }} onClick={() => openActivityViaHistoryLink("refund")}><LinkIcon /></td>
                    </tr>
                  </>
                )
              }

              else if (key == "Cross Allocation") {
                const values = associatedActivities[key];
                const latest = values.reduce((latest, current) => {
                  return new Date(current.created_at) > new Date(latest.created_at) ? current : latest;
                }, values[0]);
                return (
                  <>
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff' }}>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{"Cross-Allocation"}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.ca_change}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.ca_amt}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.workflow_status}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.cash_allocation?.created_by?.user_name ?? ""}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{format(latest.created_at, "dd-MM-yyyy HH:mm:ss")}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }} onClick={() => openActivityViaHistoryLink("cross-allocation")}><LinkIcon /></td>
                    </tr>
                  </>
                )
              }

              else if (key == "CFI") {
                const values = associatedActivities[key];
                const latest = values.reduce((latest, current) => {
                  return new Date(current.created_at) > new Date(latest.created_at) ? current : latest;
                }, values[0]);
                return (
                  <>
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff' }}>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{"CFI"}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.cfi_change}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.cfi_amt}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.workflow_status}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.cash_allocation?.created_by?.user_name ?? ""}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{format(latest.created_at, "dd-MM-yyyy HH:mm:ss")}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }} onClick={() => openActivityViaHistoryLink("cfi")}><LinkIcon /></td>
                    </tr>
                  </>
                )
              }

              else if (key == "Corrective Transfer") {
                const values = associatedActivities[key];
                const latest = values.reduce((latest, current) => {
                  return new Date(current.created_at) > new Date(latest.created_at) ? current : latest;
                }, values[0]);
                return (
                  <>
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff' }}>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{"Corrective Transfer"}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.transfer_pt_bank_account_name}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.transfer_amt}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.workflow_status}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{latest.cash_allocation?.created_by?.user_name ?? ""}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{format(latest.created_at, "dd-MM-yyyy HH:mm:ss")}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }} onClick={() => openActivityViaHistoryLink("corrective-transfer")}><LinkIcon /></td>
                    </tr>
                  </>
                )
              }
            }
            )}
          </tbody>
        </table>
      </Grid>
    </Grid>
  );


  const renderActivitiesSection = () => (
    <Grid container spacing={2} mt={2} paddingLeft={2} paddingRight={2} sx={{ visibility: 'visible', display: 'flex', width: '100%', color: '#333' }}>
      <Grid item xs={12} sx={{ visibility: 'visible', display: 'block', width: '100%' }}>
        <table style={{
          width: '100%', 
          borderCollapse: 'collapse', 
          visibility: 'visible', 
          display: 'table',
          fontFamily: "'Inter', 'Poppins', sans-serif",
          fontSize: "14px"
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th style={{ textAlign:'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>Activity Name</th>
              <th style={{ textAlign:'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>Old Policy No</th>
              <th style={{ textAlign:'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>Reason</th>
              <th style={{ textAlign:'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>Amount</th>
              <th style={{ textAlign:'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>Cancelled Date</th>
              <th style={{ textAlign:'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>WF status</th>
              <th style={{ textAlign:'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>Edited By</th>
              <th style={{ textAlign:'left', padding: '12px', border: '1px solid #ddd', color: '#333', fontWeight: 600, fontSize: '14px' }}>Edited At</th>
            </tr>
          </thead>
          <tbody>
            {policy?.deleted_ca &&
              Object.entries(policy.deleted_ca.activities).map(([activityName, arr]) =>
                arr.map((item, idx) => {
                  const workflowItem = policy.deleted_ca.workflow_data?.find(wf => wf.id === item.id);
                  return (
                    <tr key={`${activityName}-${idx}`} style={{ backgroundColor: idx % 2 === 0 ? '#fafafa' : '#ffffff' }}>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{activityName}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{policy.deleted_ca.policy_number}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{workflowItem?.writeoff_reason ?? ""}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{numberFormatter(workflowItem?.writeoff_amt ?? 0)}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{customDateFormat(policy?.deleted_time)}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{workflowItem?.workflow?.workflow_status ?? ""}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{policy.created_by?.user_name ?? ""}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', color: '#333', fontSize: '14px' }}>{format(policy.deleted_time, "dd-MM-yyyy HH:mm:ss")}</td>
                    </tr>
                  );
                })
              )
            }
          </tbody>
        </table>
      </Grid>
    </Grid>
  );

  return (
    <div style={{
      display: 'block', 
      paddingTop: "20px",
      paddingBottom: "20px",
      visibility: 'visible', 
      opacity: 1, 
      width: '100%',
      minHeight: '100%',
      height: 'auto',
      position: 'relative', 
      zIndex: 10,
      fontFamily: "'Inter', 'Poppins', sans-serif",
      fontSize: "14px"
    }}>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12}>
          <ToggleButtonGroup
            value={activeSection}
            exclusive
            onChange={handleSectionChange}
            aria-label="section toggle"
            sx={{ 
              mb: 2,
              '& .MuiToggleButton-root': {
                border: '1px solid #e0e0e0',
                borderRadius: 0,
                color: '#333',
                //backgroundColor: '#ffffff',
                height: '48px',
                padding: '12px 24px',
                fontSize: '16px',
    
                fontFamily: "'Inter', 'Poppins', sans-serif",
                fontWeight: 500,
                
                '&.Mui-selected': {
                  backgroundColor: '#1672DE',
                  color: '#ffffff',
                  borderColor: '#1672DE',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  }
                },
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
                '&:first-of-type': {
                  borderTopLeftRadius: '8px',
                  borderBottomLeftRadius: '8px',
                  borderRight: '1px solid #e0e0e0',
                },
                '&:last-of-type': {
                  borderTopRightRadius: '8px',
                  borderBottomRightRadius: '8px',
                },
              }
            }}
          >
            <ToggleButton value="information" aria-label="information section">
              Information
            </ToggleButton>
            <ToggleButton value="audit" aria-label="audit trail section">
              Audit Trail
            </ToggleButton>
            <ToggleButton value="associated_activities" aria-label="associated activities section">
              Associated Activities
            </ToggleButton>
            <ToggleButton value="activities" aria-label="activities section">
              Deleted Activities
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>

      {activeSection === 'information' && renderInformationSection()}
      {activeSection === 'audit' && renderAuditSection()}
      {activeSection === 'associated_activities' && renderAssociatedActivitiesSection()}
      {activeSection === 'activities' && renderActivitiesSection()}
    </div>
  );
};

export default AllocationDetails;
